/**
 * The score fields this reads, rather than a whole match type: the tournament pick card
 * (`SeasonMatchPickCard`) needs the same treatment and carries `ApiMatch`, not `WorldCupMatch`
 * (AC-14.6). Widening the parameter is what lets one helper answer for both screens instead of
 * the two disagreeing about the same fixture, which is the defect this closes.
 */
export interface FinishedScoreSource {
  homeScore?: number | null;
  awayScore?: number | null;
  fullTimeHomeScore?: number | null;
  fullTimeAwayScore?: number | null;
  penaltyHomeScore?: number | null;
  penaltyAwayScore?: number | null;
}

/**
 * Resolve how a finished match's score should be displayed.
 *
 * Competition picks are scored on the 90-minute result (`fullTime*`), but a knockout decided in
 * extra time has a different FINAL score (`homeScore`/`awayScore`, incl. ET) and may have been
 * settled on penalties. To avoid a card that shows "3–3 ✓ exact" for a 2–2 prediction, we surface
 * BOTH lines when they diverge: the 90-minute line the pick was judged on, and the real final.
 */
export interface FinishedScoreSingle {
  kind: 'single';
  home: number;
  away: number;
}

export interface FinishedScoreExtraTime {
  kind: 'extraTime';
  /** The 90-minute result the pick was scored on. */
  regulation: { home: number; away: number };
  /** The final score, including extra time. */
  final: { home: number; away: number };
  /** Penalty-shootout score, when decided on penalties. */
  penalty: { home: number; away: number } | null;
}

export type FinishedScoreDisplay = FinishedScoreSingle | FinishedScoreExtraTime;

const isNum = (v: number | null | undefined): v is number => typeof v === 'number';

/**
 * @returns a single score line for normal matches, or both lines when the match went beyond 90'.
 */
export function getFinishedScoreDisplay(match: FinishedScoreSource): FinishedScoreDisplay {
  const finalHome = match.homeScore ?? 0;
  const finalAway = match.awayScore ?? 0;

  const hasRegulation = isNum(match.fullTimeHomeScore) && isNum(match.fullTimeAwayScore);
  const hasPenalties = isNum(match.penaltyHomeScore) && isNum(match.penaltyAwayScore);

  // Went beyond 90' if the regulation score diverges from the final, or it was decided on pens.
  const regulationDiffers = hasRegulation
    && (match.fullTimeHomeScore !== finalHome || match.fullTimeAwayScore !== finalAway);

  if (hasRegulation && (regulationDiffers || hasPenalties)) {
    return {
      kind: 'extraTime',
      regulation: { home: match.fullTimeHomeScore as number, away: match.fullTimeAwayScore as number },
      final: { home: finalHome, away: finalAway },
      penalty: hasPenalties
        ? { home: match.penaltyHomeScore as number, away: match.penaltyAwayScore as number }
        : null,
    };
  }

  return { kind: 'single', home: finalHome, away: finalAway };
}
